import { Context, createContext, FunctionalComponent } from 'preact';
import { useLayoutEffect, useEffect, useCallback, useContext, useMemo, useState, useRef } from 'preact/hooks';
import { logger } from '@accedo/logger';
import { usePageNavigation } from '@accedo/page-navigation';
import { ServiceManager } from '##/services/elevate/manager';
import { User } from '##/dataModels/user';
import { addPublicPath } from '##/services/routing';

interface AuthCredentials {
  email: string;
  password: string;
}

interface AuthContextProps {
  isAuthenticated: boolean;
  user: User | null;
  setUser: (newState: User | null) => void;
  logOut: () => void;
  signInWithCredentials: (credentials: AuthCredentials) => Promise<void>;
}

const LP = '[Auth Context]';
const defaultContextValue: AuthContextProps = {
  isAuthenticated: false,
  user: null,
  logOut: () => {
    /** Default logout function. */
  },
  setUser: () => null,
  signInWithCredentials: () => Promise.resolve(),
};

const AuthContext: Context<AuthContextProps> = createContext(defaultContextValue);

const AuthContextProvider: FunctionalComponent = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const { path, navigateTo } = usePageNavigation();
  const lastValidatedPath = useRef<string | null>(null);
  const isValidating = useRef(false);

  const signInWithCredentials = useCallback(async ({ email, password }: AuthCredentials) => {
    try {
      const user = await ServiceManager.getIDPService()?.login({
        username: email,
        password,
        email,
      });
      if (user) {
        setUser(user);
      } else {
        setUser(null);
      }
    } catch (err) {
      throw new Error(`${err}`);
    }
  }, []);

  /**
   * Starts the auth process to check if there is already a valid session.
   */
  const validateAuth = useCallback(async (): Promise<boolean> => {
    try {
      let idpUser = ServiceManager.getIDPService()?.getUser();
      if (idpUser) {
        const { expiration } = idpUser.authToken;
        const now = Date.now();

        if (expiration < now) {
          idpUser = await ServiceManager.getIDPService()?.refresh({ token: idpUser.refreshToken.token });
          if (idpUser) {
            setUser(idpUser);
            return true;
          }
          setUser(null);
          return false;
        }
        if (!user || user.authToken.token !== idpUser.authToken.token) {
          setUser(idpUser);
        }
        return true;
      }
    } catch (err: unknown) {
      logger.warn(`${LP} Error validating the current auth state: `, err);
      setUser(null);
      return false;
    }
    return false;
  }, [user]);

  const logOut = useCallback(async () => {
    const authToken = user?.authToken.token;
    setUser(null);
    await ServiceManager.getSubscriptionService()?.clearSubscriptionsCache();
    await ServiceManager.getIDPService()?.logout({ token: authToken });
  }, [user]);

  // Force logout and redirect to landing when background token refresh fails
  useEffect(() => {
    const handleRefreshFailed = async (): Promise<void> => {
      logger.warn(`${LP} Background token refresh failed — logging user out`);
      await logOut();
      navigateTo(addPublicPath('/landing'), { replace: true });
    };
    globalThis.addEventListener('nzos:auth:refresh-failed', handleRefreshFailed);
    return () => globalThis.removeEventListener('nzos:auth:refresh-failed', handleRefreshFailed);
  }, [navigateTo, logOut]);

  // Sync new token into React state when background refresh succeeds
  useEffect(() => {
    const handleTokenRefreshed = (event: Event): void => {
      const refreshedUser = (event as CustomEvent<User>).detail;
      logger.debug(`${LP} Background token refresh succeeded — syncing new token into user state`);
      setUser(refreshedUser);
    };
    globalThis.addEventListener('nzos:auth:token-refreshed', handleTokenRefreshed);
    return () => globalThis.removeEventListener('nzos:auth:token-refreshed', handleTokenRefreshed);
  }, []);

  // Re-validate auth on visibility change (e.g. wake from sleep) — the OS/browser can
  // pause setTimeout timers, leaving an expired token until the user next navigates.
  useEffect(() => {
    const handleVisibilityChange = (): void => {
      if (document.visibilityState === 'visible') {
        logger.debug(`${LP} App became visible — re-validating auth`);
        lastValidatedPath.current = null;
        if (isValidating.current) {
          return;
        }
        isValidating.current = true;
        validateAuth()
          .catch((err) => logger.warn(`${LP} validateAuth on visibility change failed: ${err}`))
          .finally(() => {
            isValidating.current = false;
          });
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [validateAuth]);

  useLayoutEffect(() => {
    logger.debug(`${LP} useLayoutEffect`);

    // Prevent redundant validation for the same path
    if (lastValidatedPath.current === path || isValidating.current) {
      return;
    }
    isValidating.current = true;

    const isTestPath = path.startsWith(addPublicPath('/test'));
    // no auth checking for test pages
    if (!isTestPath) {
      const isLandingPath = path === addPublicPath('/landing');
      const isLoginPath = path === addPublicPath('/login');
      const isSignupPath = path === addPublicPath('/signup');
      const isUnauthenticatedPath = isLandingPath || isLoginPath || isSignupPath;

      if (isUnauthenticatedPath) {
        navigateTo(path, { replace: true });
        isValidating.current = false;
        lastValidatedPath.current = path;
      } else {
        validateAuth().then((validAuth) => {
          if (!validAuth && !isUnauthenticatedPath) {
            navigateTo(addPublicPath('/landing'), { replace: true });
          }
          isValidating.current = false;
          lastValidatedPath.current = path;
        });
      }
    }
  }, [navigateTo, path, user, validateAuth]);

  const value: AuthContextProps = useMemo(
    () => ({
      isAuthenticated: !!user,
      user,
      logOut,
      setUser,
      signInWithCredentials,
    }),
    [user, logOut, signInWithCredentials],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuthContext = (): AuthContextProps => useContext(AuthContext);

export default AuthContextProvider;
